import React, { useState, useEffect } from "react";
import * as Popover from "@radix-ui/react-popover";
import {
  format,
  parse,
  isValid,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
} from "date-fns";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, AlertCircle } from "lucide-react";

interface Props {
  defaultValue?: Date;
  onChange?: (date: Date | null) => void;
  label?: string;
}

export const DatePicker: React.FC<Props> = ({ defaultValue = new Date(), onChange = () => {}, label = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Internal state for calendar navigation
  const [viewDate, setViewDate] = useState(new Date());

  // Format constant for consistency
  const DATE_FORMAT = "dd/MM/yyyy";

  // Sync internal input value with external value prop
  useEffect(() => {
    if (defaultValue && isValid(defaultValue)) {
      setInputValue(format(defaultValue, DATE_FORMAT));
      setViewDate(defaultValue);
    } else {
      setInputValue("");
    }
  }, [defaultValue]);

  // Handle manual text input with masking
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, ""); // Remove non-digits

    // Apply DD/MM/YYYY mask
    if (val.length > 2 && val.length <= 4) {
      val = val.slice(0, 2) + "/" + val.slice(2);
    } else if (val.length > 4) {
      val = val.slice(0, 2) + "/" + val.slice(2, 4) + "/" + val.slice(4, 8);
    }

    setInputValue(val);

    // Attempt to parse once the string is complete
    if (val.length === 10) {
      const parsed = parse(val, DATE_FORMAT, new Date());
      if (isValid(parsed)) {
        setError(null);
        onChange(parsed);
        setViewDate(parsed);
      } else {
        setError("Invalid date");
      }
    } else {
      setError(null);
    }
  };

  const handleDateSelect = (date: Date) => {
    onChange(date);
    setInputValue(format(date, DATE_FORMAT));
    setIsOpen(false);
    setError(null);
  };

  const clearDate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange(null);
    setInputValue("");
    setError(null);
  };

  return (
    <div className="w-full space-y-2 p-1">
      {label && <label className="block text-gray-700">{label}</label>}

      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <div className="group relative rounded-md border border-gray-200 px-2">
          <div
            className={`flex w-full items-center rounded-lg bg-white shadow-sm transition-all ${error ? "border-red-500 ring-1 ring-red-500" : ""} `}
          >
            <Popover.Trigger asChild>
              <button className="py-2 text-slate-400 transition-colors">
                <CalendarIcon size={18} />
              </button>
            </Popover.Trigger>

            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={inputValue}
              onChange={handleInputChange}
              maxLength={10}
              className="flex-1 border-0 px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
            />

            {inputValue && (
              <button
                onClick={clearDate}
                className="py-2 pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {error && (
            <div className="mt-1 flex items-center gap-1 text-xs text-red-500 animate-in fade-in slide-in-from-top-1">
              <AlertCircle size={12} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <Popover.Portal>
          <Popover.Content
            className="z-50 w-72 rounded-xl border bg-white p-4 shadow-2xl animate-in fade-in zoom-in-95"
            sideOffset={8}
            align="start"
          >
            <Calendar
              selectedDate={viewDate}
              viewDate={viewDate}
              setViewDate={setViewDate}
              onSelect={handleDateSelect}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

/**
 * Internal Calendar Component using date-fns
 */
interface CalendarProps {
  selectedDate: Date | null;
  viewDate: Date;
  setViewDate: (date: Date) => void;
  onSelect: (date: Date) => void;
}

const Calendar: React.FC<CalendarProps> = ({ selectedDate, viewDate, setViewDate, onSelect }) => {
  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  // Generate all days to display in the grid (including padding from prev/next months)
  const calendarDays = eachDayOfInterval({
    start: startDate,
    end: endDate,
  });

  const prevMonth = () => setViewDate(subMonths(viewDate, 1));
  const nextMonth = () => setViewDate(addMonths(viewDate, 1));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white">{format(viewDate, "MMMM yyyy")}</h2>
        <div className="flex gap-1">
          <button
            onClick={prevMonth}
            className="rounded p-1 text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={nextMonth}
            className="rounded p-1 text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(day => (
          <div
            key={day}
            className="flex h-8 items-center justify-center text-[10px] font-bold uppercase tracking-wider text-slate-400"
          >
            {day}
          </div>
        ))}

        {calendarDays.map(date => {
          const isSelected = selectedDate && isSameDay(date, selectedDate);
          const isCurrentMonth = isSameMonth(date, monthStart);
          const isCurrentDay = isToday(date);

          return (
            <button
              key={date.toString()}
              onClick={() => onSelect(date)}
              className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs transition-all ${
                isSelected
                  ? "bg-blue-600 font-bold text-white shadow-md shadow-blue-500/20"
                  : "hover:bg-slate-100 dark:hover:bg-slate-800"
              } ${!isSelected && isCurrentMonth ? "text-slate-700 dark:text-slate-200" : ""} ${!isSelected && !isCurrentMonth ? "text-slate-300 dark:text-slate-600" : ""} ${isCurrentDay && !isSelected ? "font-medium text-blue-600 ring-1 ring-blue-500" : ""} `}
            >
              {format(date, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
};
